// src/components/pitch/AssignmentPanel.tsx

import { useAuth } from '@/contexts/AuthContext';
import { Pitch } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { UserCheck, Calendar, User, Clock } from 'lucide-react';

interface Props {
  pitch: Pitch;
}

const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export default function AssignmentPanel({ pitch }: Props) {
  const { user, allUsers } = useAuth();
  const isSenior = user?.role === 'senior_analyst';
  const isFounder = user?.role === 'founder';

  // Check if pitch is already assigned
  const isAssigned = !!pitch.assignedAnalystId;
  const assignedAnalyst = isAssigned ? allUsers.find(u => u.id === pitch.assignedAnalystId) : null;
  const assignedBySenior = pitch.assignedBySeniorId ? allUsers.find(u => u.id === pitch.assignedBySeniorId) : null;

  // CASE 1: Pitch is assigned - SHOW ASSIGNMENT DETAILS
  if (isAssigned && assignedAnalyst) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-green-600" />
            Pitch Assignment
          </CardTitle>
          <CardDescription>
            This pitch has been assigned to a Business Analyst
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="text-lg">
                {getInitials(assignedAnalyst.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-lg">{assignedAnalyst.name}</p>
              <p className="text-sm text-muted-foreground">{assignedAnalyst.email}</p>
              <Badge className="mt-1 bg-green-100 text-green-700 border-green-200">
                Business Analyst
              </Badge>
            </div>
          </div>

          {pitch.assignedAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Assigned on: {new Date(pitch.assignedAt).toLocaleDateString()}</span>
            </div>
          )}

          {assignedBySenior && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              <span>Assigned by: {assignedBySenior.name}</span>
            </div>
          )}

          {isSenior && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p>This pitch is currently being evaluated by {assignedAnalyst.name}.</p>
            </div>
          )}

          {isFounder && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p>Your pitch is currently being reviewed by {assignedAnalyst.name}.</p>
              <p className="text-xs mt-1">You will be notified when there are updates.</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // CASE 2: Pitch is NOT assigned - SHOW STATUS ONLY
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-yellow-600" />
          Assignment Status
        </CardTitle>
        <CardDescription>
          This pitch is awaiting assignment
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
          <Clock className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
          <p className="text-yellow-700">This pitch has not been assigned yet.</p>
          {isSenior && (
            <p className="text-xs text-yellow-600 mt-2">
              Use the Pitch Management page to assign this pitch.
            </p>
          )}
          {isFounder && (
            <p className="text-xs text-yellow-600 mt-2">
              A Senior Analyst will assign this pitch to a Business Analyst soon.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}